/**
 * Store-sorted grocery list (PLAN §4.5).
 *
 * Sort key per item: `(department_rank, aisle_number, endcap_flag, name)`.
 * The department rank comes from the household's own department order for the
 * store (drag-reordered once and remembered); departments the household has
 * not ordered come after the ones it has, and items whose department is
 * unknown come last. Within a department, locations sort by their number the
 * way a shopper walks the store — "aisle 2" before "aisle 12" — with an
 * endcap immediately after the aisle it hangs off. Items with no location at
 * all trail their department, ordered by name.
 *
 * The key is a plain string so the API can persist it (`groceryItemDtoSchema.sortKey`)
 * and clients can re-sort without re-deriving anything.
 */
import { DEPARTMENTS, } from './types.js';
/** Department an ingredient category falls in when the store knows nothing better. */
export const CATEGORY_DEPARTMENT = {
    produce: 'produce',
    meat: 'butcher',
    seafood: 'seafood',
    dairy: 'dairy',
    bakery: 'bakery',
    pantry: 'aisles',
    frozen: 'frozen',
    spices: 'aisles',
    beverages: 'aisles',
    other: 'other',
};
/** Rank for a department the household has not put in its order, still grouped. */
const UNORDERED_BASE = 50;
/** Rank for "no idea which department" — always last. */
const UNKNOWN_RANK = 99;
/** Sorts after every digit and letter, so unplaced items trail their department. */
const NO_LOCATION = '~';
const LOCATION_TYPE_RANK = {
    aisle: '0',
    endcap: '1',
    department: '2',
};
function isDepartment(value) {
    return DEPARTMENTS.includes(value);
}
/** Department for an item: explicit → named by the location → category → unknown. */
export function departmentOf(item, location) {
    if (location?.department !== undefined)
        return location.department;
    if (location?.type === 'department') {
        const named = location.value.trim().toLowerCase();
        if (isDepartment(named))
            return named;
    }
    if (item.category != null)
        return CATEGORY_DEPARTMENT[item.category];
    return null;
}
function departmentRank(department, order) {
    if (department === null)
        return UNKNOWN_RANK;
    const index = order.indexOf(department);
    if (index >= 0)
        return index;
    return UNORDERED_BASE + DEPARTMENTS.indexOf(department);
}
/**
 * Digits compare numerically, everything else as text: "12" sorts after "2",
 * and "produce" sorts after both (digits precede letters in ASCII).
 */
function naturalKey(value) {
    const parts = value
        .trim()
        .toLowerCase()
        .match(/\d+|\D+/g);
    if (parts === null)
        return '';
    return parts.map((part) => (/^\d/.test(part) ? part.padStart(12, '0') : part)).join('');
}
function nameOf(item, options) {
    const name = item.ingredientName ?? options.nameByIngredient?.[item.ingredientId];
    return (name ?? item.ingredientId).trim().toLowerCase();
}
function locationOf(item, options) {
    return item.location ?? options.locationByIngredient?.[item.ingredientId] ?? null;
}
function decorate(item, options) {
    const location = locationOf(item, options);
    const department = departmentOf(item, location);
    const rank = departmentRank(department, options.departmentOrder ?? DEPARTMENTS);
    const place = location === null
        ? `${NO_LOCATION}|9`
        : `${naturalKey(location.value)}|${LOCATION_TYPE_RANK[location.type]}`;
    return {
        sortKey: `${String(rank).padStart(2, '0')}|${place}|${nameOf(item, options)}`,
        department,
    };
}
/** The sort key for a single item — same rules as `sortListForStore`. */
export function computeSortKey(item, options = {}) {
    return decorate(item, options).sortKey;
}
/**
 * Sort a list the way the household walks the store, tagging every item with
 * its `sortKey` and resolved `department`. Deterministic: equal keys fall back
 * to the ingredient id.
 */
export function sortListForStore(items, options = {}) {
    const decorated = items.map((item) => ({ ...item, ...decorate(item, options) }));
    decorated.sort((a, b) => {
        if (a.sortKey !== b.sortKey)
            return a.sortKey < b.sortKey ? -1 : 1;
        return a.ingredientId < b.ingredientId ? -1 : 1;
    });
    return decorated;
}
//# sourceMappingURL=sort.js.map